import { useQuery } from "@tanstack/react-query";
import { Card, Drawer, Typography } from "antd";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { apiFetch, withQuery } from "../api/client";
import type { CongregationContactRow, CoordinatorRow, ListResponse } from "../api/types";
import { DataTable } from "../components/DataTable";
import { FilterBar } from "../components/FilterBar";
import type { FilterValues } from "../components/FilterBar";
import { useCongregationContactColumns, useCoordinatorColumns } from "../components/domainColumns";

/**
 * Sidebar entry "congregation_contacts" → /congregation/contacts.
 *
 * The same idea as the circumscription contacts one level down: every active
 * congregation with its named contact, and behind each row the local
 * coordinators marked as contact persons.
 */
export function CongregationContactsPage() {
  const { t } = useTranslation();
  const [filters, setFilters] = useState<FilterValues>({});
  const [selected, setSelected] = useState<CongregationContactRow | null>(null);

  const contactColumns = useCongregationContactColumns();
  const coordinatorColumns = useCoordinatorColumns();

  const contacts = useQuery({
    queryKey: ["congregation", "contacts", filters],
    queryFn: () =>
      apiFetch<ListResponse<CongregationContactRow>>(withQuery("/congregation/contacts", filters)),
  });

  const coordinators = useQuery({
    queryKey: ["congregation", "coordinators", selected?.id],
    queryFn: () =>
      apiFetch<ListResponse<CoordinatorRow>>(`/congregation/contacts/${selected!.id}/coordinators`),
    enabled: selected !== null,
  });

  const fields = useMemo(
    () => [
      // Each of these is matched against a denomination and a code at once.
      { name: "circumscription", label: t("activerecord.models.circumscription") },
      { name: "congregation", label: t("activerecord.models.congregation") },
      { name: "language_code", label: t("activerecord.attributes.language.language_code") },
    ],
    [t],
  );

  return (
    <Card title={t("navigation.congregation_contacts")}>
      <FilterBar fields={fields} onApply={setFilters} loading={contacts.isFetching} />

      <DataTable
        columns={contactColumns}
        rows={contacts.data?.data}
        loading={contacts.isPending}
        error={contacts.error}
        onRowClick={setSelected}
        exportAs={t("navigation.congregation_contacts")}
      />

      <Drawer
        open={selected !== null}
        onClose={() => setSelected(null)}
        size={860}
        destroyOnHidden
        title={
          selected && (
            <Typography.Text>
              {[selected.congregation_code, selected.denomination].filter(Boolean).join(" – ")}
            </Typography.Text>
          )
        }
      >
        <Typography.Title level={5}>{t("congregation.coordinator")}</Typography.Title>

        {/* Named after the congregation for the same reason as on the
            circumscription list. */}
        <DataTable
          columns={coordinatorColumns}
          rows={coordinators.data?.data}
          exportAs={[selected?.congregation_code, t("congregation.coordinator")]
            .filter(Boolean)
            .join(" · ")}
          loading={coordinators.isPending}
          error={coordinators.error}
          paginated={false}
        />
      </Drawer>
    </Card>
  );
}
